import React, { useState, useEffect } from "react";
import { Modal, Button, InputNumber, notification } from "antd";
import { Ico } from "components/Uikit";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { AudioTrack } from "providers/AudioProvider/types";
import { phrasePieceReplaceNote } from "providers/AudioProvider/helpers";
import { useUiLang } from "utils/uiLanguage";
import {
    ContainerSC,
    TimeInputContainerSC,
    TimeInputGroupSC,
    SubtitleListSC,
    SubtitleItemSC,
    WarningTextSC,
    PickButtonSC,
} from "./styled";
import { replaceByInterval } from "./replaceByInterval";

export const CreateSubtitleByInterval = () => {
    const handles = useAudioHandlesContext();
    const state = useAudioStateContext();
    const en = useUiLang() === "en";
    const [open, setOpen] = useState(false);

    const [startMin, setStartMin] = useState<number>(0);
    const [startSec, setStartSec] = useState<number>(0);
    const [endMin, setEndMin] = useState<number>(0);
    const [endSec, setEndSec] = useState<number>(0);

    const [checked, setChecked] = useState(false);
    const [foundSubtitles, setFoundSubtitles] = useState<AudioTrack[]>([]);
    const [loading, setLoading] = useState(false);
    const [pickingField, setPickingField] = useState<"start" | "end" | null>(null);

    useEffect(() => {
        if (!open || !pickingField) return;

        const handler = (e: Event) => {
            const timeMs = (e as CustomEvent).detail.timeMs;
            const totalSec = Math.floor(timeMs / 1000);
            const min = Math.floor(totalSec / 60);
            const sec = totalSec % 60;

            if (pickingField === "start") {
                setStartMin(min);
                setStartSec(sec);
            } else {
                setEndMin(min);
                setEndSec(sec);
            }
            setChecked(false);
            setPickingField(null);
        };

        window.addEventListener("waveform-time-click", handler);
        return () => window.removeEventListener("waveform-time-click", handler);
    }, [open, pickingField]);

    const checkSubtitles = () => {
        const startMs = (startMin * 60 + startSec) * 1000;
        const endMs = (endMin * 60 + endSec) * 1000;

        if (endMs <= startMs) {
            notification.error({ message: "End time must be greater than start time" });
            return;
        }

        const found =
            state.translationTrackAll[state.originalLang]?.filter((track) => {
                const trackEnd = track.start + track.length;
                // Check for overlapping intervals
                // Track starts inside interval OR Track ends inside interval OR Track covers interval
                return (
                    (track.start >= startMs && track.start < endMs) ||
                    (trackEnd > startMs && trackEnd <= endMs) ||
                    (track.start <= startMs && trackEnd >= endMs)
                );
            }) || [];

        setFoundSubtitles(found);
        setChecked(true);
    };

    const handleCreate = async () => {
        const startMs = (startMin * 60 + startSec) * 1000;
        const endMs = (endMin * 60 + endSec) * 1000;

        try {
            setLoading(true);

            // Сначала новые реплики, потом удаление старых: раньше старые удалялись ДО
            // распознавания, и «нет речи» или сбой оставляли промежуток пустым.
            const result = await replaceByInterval({
                start: startMs,
                end: endMs,
                overlapping: foundSubtitles.map((t) => t.original_id),
                handles,
            });

            if (result.error && !result.created) {
                notification.error({
                    message: "Subtitles were not created — the old ones are kept",
                    description: result.error,
                });
            } else if (!result.created) {
                notification.warning({
                    message: "No speech recognised in this interval",
                    description: "Nothing was created, and the existing subtitles are kept.",
                });
            } else if (result.error) {
                notification.error({
                    message: `Created ${result.created} subtitles, but the old ones were not all deleted`,
                    description: result.error,
                });
            } else {
                notification.success({
                    message: "Success",
                    description: `Created ${result.created} subtitles${
                        result.removed ? `, replaced ${result.removed}` : ""
                    }`,
                });
                handleClose();
            }
        } catch (e) {
            notification.error({ message: "Unexpected error" });
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setChecked(false);
        setFoundSubtitles([]);
        setStartMin(0);
        setStartSec(0);
        setEndMin(0);
        setEndSec(0);
        setPickingField(null);
    };

    const confirmCreate = () => {
        if (foundSubtitles.length > 0) {
            // Удаление здесь идёт без второго вопроса (confirmed: true), поэтому куски фраз среди
            // удаляемых названы тут же, с их переводом: со стороны они выглядят дублями.
            const pieceNote = phrasePieceReplaceNote(
                state.translationTrackAll[state.originalLang],
                foundSubtitles.map((t) => t.original_id),
                en,
            );
            Modal.confirm({
                title: "Warning: Overlapping subtitles found!",
                content: (
                    <div>
                        <p>
                            Found <b>{foundSubtitles.length}</b> subtitles in this interval.
                        </p>
                        <p style={{ color: "red" }}>
                            They will be DELETED and replaced by new transcription — only after
                            the new subtitles are created. If no speech is recognised, they stay.
                        </p>
                        {pieceNote && (
                            <p data-phrase-pieces="true" style={{ whiteSpace: "pre-line" }}>
                                {pieceNote}
                            </p>
                        )}
                        <p>Are you sure?</p>
                    </div>
                ),
                okText: "Delete & Create",
                okType: "danger",
                onOk: handleCreate,
            });
        } else {
            handleCreate();
        }
    };

    const formatTime = (ms: number) => {
        const totalSec = Math.floor(ms / 1000);
        const m = Math.floor(totalSec / 60);
        const s = totalSec % 60;
        return `${m}:${s.toString().padStart(2, "0")}`;
    };

    return (
        <>
            <div
                className="x_player_video_set_1"
                onClick={() => setOpen(true)}
                title="Create Subtitles by Interval"
            >
                <div className="ico">add_circle_outline</div>
            </div>

            <Modal
                open={open}
                title="Create Subtitles by Interval"
                onCancel={handleClose}
                footer={null}
                width={500}
                mask={false}
                styles={{
                    wrapper: { pointerEvents: "none" },
                    content: {
                        pointerEvents: "auto",
                        boxShadow: "0 6px 16px 0 rgba(0, 0, 0, 0.2)",
                    },
                }}
            >
                <ContainerSC>
                    <TimeInputContainerSC>
                        <span>Start:</span>
                        <TimeInputGroupSC>
                            <InputNumber
                                min={0}
                                value={startMin}
                                onChange={(v) => {
                                    setStartMin(v || 0);
                                    setChecked(false);
                                }}
                                placeholder="Min"
                            />
                            <span>m</span>
                            <InputNumber
                                min={0}
                                max={59}
                                value={startSec}
                                onChange={(v) => {
                                    setStartSec(v || 0);
                                    setChecked(false);
                                }}
                                placeholder="Sec"
                            />
                            <span>s</span>
                        </TimeInputGroupSC>
                        <PickButtonSC
                            $active={pickingField === "start"}
                            onClick={() =>
                                setPickingField(pickingField === "start" ? null : "start")
                            }
                            title="Pick start time from audio waveform"
                        >
                            <Ico>my_location</Ico>
                        </PickButtonSC>
                    </TimeInputContainerSC>

                    <TimeInputContainerSC>
                        <span>End:</span>
                        <TimeInputGroupSC>
                            <InputNumber
                                min={0}
                                value={endMin}
                                onChange={(v) => {
                                    setEndMin(v || 0);
                                    setChecked(false);
                                }}
                                placeholder="Min"
                            />
                            <span>m</span>
                            <InputNumber
                                min={0}
                                max={59}
                                value={endSec}
                                onChange={(v) => {
                                    setEndSec(v || 0);
                                    setChecked(false);
                                }}
                                placeholder="Sec"
                            />
                            <span>s</span>
                        </TimeInputGroupSC>
                        <PickButtonSC
                            $active={pickingField === "end"}
                            onClick={() => setPickingField(pickingField === "end" ? null : "end")}
                            title="Pick end time from audio waveform"
                        >
                            <Ico>my_location</Ico>
                        </PickButtonSC>
                    </TimeInputContainerSC>

                    {!checked && (
                        <Button type="primary" onClick={checkSubtitles} block>
                            Check Interval
                        </Button>
                    )}

                    {checked && (
                        <>
                            {foundSubtitles.length > 0 ? (
                                <>
                                    <WarningTextSC>
                                        <Ico>warning</Ico>
                                        Found {foundSubtitles.length} existing subtitles in this
                                        range:
                                    </WarningTextSC>
                                    <SubtitleListSC>
                                        {foundSubtitles.map((sub) => (
                                            <SubtitleItemSC key={sub.id}>
                                                <span className="time">
                                                    [{formatTime(sub.start)} -{" "}
                                                    {formatTime(sub.start + sub.length)}]
                                                </span>
                                                {sub.captions}
                                            </SubtitleItemSC>
                                        ))}
                                    </SubtitleListSC>
                                </>
                            ) : (
                                <div style={{ color: "green", textAlign: "center" }}>
                                    No overlapping subtitles found. Safe to create.
                                </div>
                            )}

                            <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                                <Button onClick={() => setChecked(false)} style={{ flex: 1 }}>
                                    Change Interval
                                </Button>
                                <Button
                                    type="primary"
                                    danger={foundSubtitles.length > 0}
                                    loading={loading}
                                    onClick={confirmCreate}
                                    style={{ flex: 1 }}
                                >
                                    {foundSubtitles.length > 0
                                        ? "Delete & Create"
                                        : "Create Subtitles"}
                                </Button>
                            </div>
                        </>
                    )}
                </ContainerSC>
            </Modal>
        </>
    );
};
